/**
 * 流水**详情抽屉**（C 域 PRD **§5.1.10**「点行开抽屉（非新页面）」· `AC-C1.33` · `AC-L3.5`）
 *
 * 展示：父子腿结构 · 分类 · 账户 · 标签 · 成员 · 来源（含导入批次号）· 创建与修改时间。
 *
 * ## 🔴 拆成「内容」与「抽屉壳」两个导出（可测性）
 * MUI `Drawer` 走 **Portal** —— 在 `renderToStaticMarkup`（本项目的端侧测试基座）下
 * **内容是否进产物取决于实现细节**。⇒ 每条业务断言都会变成"测不到"。
 * 处置（沿 ㉝ 批保单表单的做法）：把内容抽成 **`TransactionDetailContent`**（普通容器，零 Portal）
 * 让 spec 直接渲染它；`TransactionDrawer` 只负责"滑出来的那一层"。
 * 🔴 **两条纪律**：① 内容组件里**不放**任何 `Drawer`/`Modal`/`Portal`；
 * ② 折叠/展开之类交互**不放在内容组件里**（基座跑不了交互，会假绿）。
 *
 * ## 🔴 归因三行取服务端的 `attribution`，端侧不自己映射归属人
 * 「归属人」= `accounts.ownerId`，一笔多腿交易可能分属不同归属人 ⇒ 端侧各写一遍映射
 * 就是同一事实的第二份实现（铁律 24）。契约类型 `AttributionView` 在 ㊻ 批**预留**、本批**首次实装**。
 *
 * ## 🔴 时间一律按**账本时区**渲染
 * `createdAt` / `updatedAt` 是 UTC 毫秒；展示必须经 `localDayOf` / `localClockOf`（共享日历内核）——
 * 直接用 `new Date(ms)` 会在跨时区家庭里显示成"另一天的某一刻"（`local-day.ts` 文件头记过同类坑）。
 */

import {
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  Stack,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  ParentRowKind,
  SALARY_MODE_SIMPLE,
  localClockOf,
  localDayOf,
  type TransactionDetailView,
} from '@jiaying/shared';
import { getTransaction } from '../api/endpoints.js';
import { Loading } from './Loading.js';
import { TxnLegsList } from './TxnLegsList.js';
import { SOURCE_LABELS, ownersText } from '../features/journal-labels.js';
import { canRefund, refundCategoryId } from '../features/refund-entry.js';
import { DEFAULT_RETURN_TO } from '../features/return-to.js';

/** 归因三行 + 来源 —— 一行「标签：值」的小组件 */
function Field({ label, value, testId }: { label: string; value: string; testId: string }) {
  return (
    <Stack direction="row" spacing={1} sx={{ py: 0.25 }}>
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 64 }}>
        {label}
      </Typography>
      <Typography variant="body2" data-testid={testId}>
        {value}
      </Typography>
    </Stack>
  );
}

/**
 * 抽屉内容（**零 Portal · 可静态渲染**）。
 *
 * @param detail 服务端详情（含 `tags` 与 `attribution`）
 * @param timezone 账本时区（用于时间渲染 —— 由页面从 `LedgerSummary` 传入）
 */
export function TransactionDetailContent({
  detail,
  timezone,
  onEdit,
  onRefund,
}: {
  detail: TransactionDetailView;
  timezone: string;
  /**
   * 🆕 点「编辑」的回调（`DR-042` D1 · `AC-C1.41`）。
   *
   * 🔴 **由调用方注入**，本组件**不自己** `navigate` —— 两条理由：
   * ① 内容组件要保持"**零副作用 · 可静态渲染**"（本文件头两条纪律）⇒ 它只回答"按钮在不在"；
   * ② 编辑**不在抽屉内做**（否则即第二套录入形态，`AC-C1.34`）——
   *    本按钮只负责把用户**送到「记一笔」**，真正的录入形态仍是那一套。
   */
  onEdit?: () => void;
  /**
   * 🆕 批 `115`（T6 · `AC-B5.12`）：点「退款」的回调。
   *
   * 🔴 与 `onEdit` **同一契约**：由调用方注入，本组件不 `navigate`、不请求 ——
   * 内容层保持"零副作用 · 可静态渲染"（文件头两条纪律），它只回答"按钮在不在"。
   * 🔴 **要不要给这个按钮由调用方决定**（判据 = `features/refund-entry.ts` 的 `canRefund`），
   * 本组件不再判一遍形态（否则就是第二套判据）。
   */
  onRefund?: () => void;
}) {
  const a = detail.attribution;
  return (
    <Box data-testid="transaction-detail" sx={{ width: 360, p: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
        {detail.note === null || detail.note.trim() === '' ? '（无备注）' : detail.note}
      </Typography>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
          {detail.date}
          {detail.parentDeleted === true ? ' · 原交易已删除' : ''}
        </Typography>
        {onRefund !== undefined && (
          // 🆕 批 `115`（T6）：流水页的退款入口就在抽屉里 —— 与首页那颗按钮同一判据
          <Button size="small" variant="outlined" data-testid="detail-refund" onClick={onRefund}>
            退款
          </Button>
        )}
        {onEdit !== undefined && (
          <Button size="small" variant="outlined" data-testid="detail-edit" onClick={onEdit}>
            编辑
          </Button>
        )}
      </Stack>
      {/*
        🆕 批 141：**工资条「简化记账」标记**（`DR-016` §3.6 / C 域 §5.2.1⑤）。
        🔴 必须显式标出来：这笔的收入是**到手（实发）**，未含社保与个税 ——
        与完整工资条（收入按税前）不是同一口径，混着看会让"收入"忽高忽低。
      */}
      {detail.salaryMode === SALARY_MODE_SIMPLE && (
        <Chip
          size="small"
          color="warning"
          variant="outlined"
          data-testid="detail-salary-simple"
          label="简化记账 · 未含社保与个税"
          sx={{ mt: 1 }}
        />
      )}
      <Divider sx={{ my: 1 }} />

      {/* ── 🔴 归因三行（`AC-L3.5`）：归属人 / 记录人 / 时间 ── */}
      <Field label="归属" value={ownersText(a.accountOwners.map((o) => o.name))} testId="attr-owners" />
      <Field label="记录人" value={a.recorder.name} testId="attr-recorder" />
      <Field
        label="时间"
        value={`${localDayOf(a.createdAt, timezone)} ${localClockOf(a.createdAt, timezone)}`}
        testId="attr-created"
      />
      <Field label="来源" value={SOURCE_LABELS[detail.source]} testId="detail-source" />
      <Field label="最后修改" value={`${localDayOf(a.updatedAt, timezone)} ${localClockOf(a.updatedAt, timezone)}`} testId="attr-updated" />

      <Divider sx={{ my: 1 }} />
      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
        明细（含 {String(detail.legs.length)} 笔）
      </Typography>
      {/*
        🆕 批 `115`（`DR-066` D3 · `AC-B5.6`）：逐笔明细改调**共用组件** `TxnLegsList`
        —— 流水行的折叠区用同一个组件的 `compact` 变体渲染。
        🔴 修前这里是页面内的一段 `List`：两处各写一段，"同一笔的构成"就有了两份实现。
      */}
      <TxnLegsList legs={detail.legs} variant="full" />

      {detail.tagNames.length > 0 && (
        <>
          <Divider sx={{ my: 1 }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            标签
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
            {detail.tags.map((t) => (
              <Chip key={t.id} size="small" variant="outlined" label={t.name} />
            ))}
          </Box>
        </>
      )}

      {detail.parentId !== null && (
        <>
          <Divider sx={{ my: 1 }} />
          <Typography variant="caption" color="text.secondary">
            本笔为**退款**，冲减的是原交易 {detail.parentId}
            {detail.parentDeleted === true ? '（原交易已删除）' : ''}
          </Typography>
        </>
      )}

      {detail.parent.kind === ParentRowKind.Transfer && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          转账不计收支（资金搬运）
        </Typography>
      )}
    </Box>
  );
}

/**
 * 抽屉外壳：`transactionId !== null` 时滑出。
 *
 * ⚠️ 查询在**打开时**才发（`enabled`）—— 列表页不该为"可能点开的每一行"预取详情。
 * 🔴 与全项目一致：`isPending` 与 `isError` **两条分开守**（`isPending` 不排除已失败）。
 */
export function TransactionDrawer({
  transactionId,
  timezone,
  onClose,
  returnTo = DEFAULT_RETURN_TO,
}: {
  transactionId: string | null;
  timezone: string;
  onClose: () => void;
  /**
   * 🆕 批 `115`（`DR-066` §5 · T3 第 4 条）：编辑保存后**回哪一页**。
   *
   * 🔴 由调用方**显式传**（流水页传 `/journal`），不依赖默认值 —— 默认值只是兜底。
   * 抽屉自己不认识路由：它只把一个字符串塞进 `/new?returnTo=`，
   * 合法性仍由 `features/return-to.ts` 的白名单决定（口径只有一处）。
   */
  returnTo?: string;
}) {
  const navigate = useNavigate();
  const q = useQuery({
    queryKey: ['transaction', transactionId ?? ''],
    queryFn: () => getTransaction(transactionId as string),
    enabled: transactionId !== null,
  });

  return (
    <Drawer anchor="right" open={transactionId !== null} onClose={onClose}>
      {q.isPending && <Loading py={4} />}
      {q.isError && (
        <Box sx={{ p: 2 }}>
          <Typography variant="body2" color="error">
            详情加载失败，请重试
          </Typography>
        </Box>
      )}
      {q.isSuccess && (
        <TransactionDetailContent
          detail={q.data}
          timezone={timezone}
          // 🔴 编辑 = **跳「记一笔」并预填**（`DR-042` D1）—— 抽屉先关，避免两层同时开着
          onEdit={() => {
            onClose();
            navigate(
              `/new?editOf=${encodeURIComponent(q.data.id)}&returnTo=${encodeURIComponent(returnTo)}`,
            );
          }}
          /*
            🆕 批 `115`（T6 · `AC-B5.12`）：退款入口 —— **要不要给按钮由这里判**
            （`canRefund` 是首页与流水页的同一个判据）。
            🔴 跳转形状照抄首页的 `state.refund`（同一份 `RefundSource` 契约）：
            少一个字段，`refundDraft` 就会拼出一张缺账户的草稿。
          */
          onRefund={
            canRefund(q.data)
              ? () => {
                  onClose();
                  void navigate('/new', {
                    state: {
                      refund: {
                        id: q.data.id,
                        accountId: q.data.parent.mainAccountId,
                        note: q.data.note,
                        date: q.data.date,
                        net: q.data.parent.net,
                        // 🔴 批 `119`：退款腿要挂**原支出分类**（`isRefundTxn` 的判据），
                        //    少这一个字段 ⇒ 分类格是空的 ⇒ 提交必 400
                        categoryId: refundCategoryId(q.data),
                        // 🆕 批 `120`（`DR-069` D6）：已退多少 —— 退款页算「最多可退」用
                        refundedAmount: q.data.refunds?.amount ?? 0,
                      },
                    },
                  });
                }
              : undefined
          }
        />
      )}
    </Drawer>
  );
}
